<mat-card>
  <mat-card-content>
    <div class="top-banner">
      <p class="performance-year-text">
        Performance Year: <strong>{{ performanceYear }}</strong>
      </p>
    </div>

    <div class="section-header">
      <div class="title-with-status">
        <h2 class="section-title">TIN and Eligibility Details</h2>
        <div class="tin-status-chip" [ngClass]="getTinStatusClass()">
          <span class="status-text">TIN Status: {{ getTinStatusText() }}</span>
        </div>
      </div>

      @if (isSuperAdmin$ | async) {
        <button
          mat-raised-button
          color="primary"
          class="admin-button"
          (click)="showAdminActions = !showAdminActions"
          [disabled]="!currentPacket()?.tinDetails?.documentName"
        >
          Reg-ent Admin Approval
        </button>
      }
    </div>

    @if (showAdminActions && (isSuperAdmin$ | async)) {
      <div class="admin-section">
        <h3 class="admin-title">Admin Actions</h3>
        <div class="admin-actions">
          <button
            mat-raised-button
            color="primary"
            (click)="approveTin()"
            [disabled]="
              tinStatus === 'approved' ||
              !currentPacket()?.tinDetails?.documentName
            "
          >
            <mat-icon>check_circle</mat-icon>
            Approve TIN
          </button>
          <div class="reject-button-wrapper">
            <button
              mat-raised-button
              color="warn"
              (click)="rejectTin()"
              [disabled]="!(canReject$ | async) || tinStatus === 'rejected'"
            >
              <mat-icon>cancel</mat-icon>
              Reject TIN
            </button>
            @if (showRejectEmailWarning()) {
              <mat-icon
                class="email-warning-icon"
                [matTooltip]="getRejectButtonTooltip()"
                color="warn"
              >
                info
              </mat-icon>
            }
          </div>
        </div>
      </div>
      <mat-divider class="admin-divider"></mat-divider>
    }

    <form [formGroup]="tinDetailsForm" class="details-form">
      <div class="left-column">
        <div class="info-section">
          <h3 class="info-header">Tax Identification Number (TIN)</h3>
          <div class="info-value">{{ selectedTin }}</div>
        </div>

        <div class="info-section">
          <h3 class="info-header">{{ relatedOrganizations().length > 0 ? 'TIN Rollup' : 'Practice' }}</h3>
          <div class="info-value">{{ practiceOrgName }}</div>
          @for (org of relatedOrganizations(); track org.organizationId) {
            <div class="info-value">{{ org.organizationName }}</div>
          }
        </div>

        <div class="eligibility-display">
          <div class="eligibility-header">
            <span class="eligibility-label">MIPS Eligibility:</span>
            <a class="eligibility-link" (click)="openQppSite()">
              To View MIPS Eligibility Details Click Here
            </a>
          </div>

          <div class="eligibility-list">
            @if (isIndividualEligible()) {
              <span class="eligibility-item">Individual</span>
            }
            @if (isGroupEligible()) {
              <span class="eligibility-item">Group</span>
            }
            @if (isSubgroupEligible()) {
              <span class="eligibility-item">Subgroup</span>
            }
          </div>
        </div>

        <div class="field-row">
          <mat-form-field appearance="outline">
            <mat-label>Email ID for Feedback Report Distribution</mat-label>
            <input
              matInput
              formControlName="emailForFeedback"
              placeholder=""
              type="email"
            />
            @if (isFieldInvalid("emailForFeedback")) {
              <mat-error>Please enter a valid email address</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Confirm Email ID</mat-label>
            <input
              matInput
              formControlName="confirmEmailForFeedback"
              placeholder=""
              type="email"
            />
            @if (
              tinDetailsForm
                .get("confirmEmailForFeedback")
                ?.hasError("emailMismatch")
            ) {
              <mat-error>Email addresses do not match</mat-error>
            }
            @if (
              isFieldInvalid("confirmEmailForFeedback") &&
              !tinDetailsForm
                .get("confirmEmailForFeedback")
                ?.hasError("emailMismatch")
            ) {
              <mat-error>Please confirm your email address</mat-error>
            }
          </mat-form-field>
        </div>
      </div>

      <div class="right-column">
        <div class="date-row">
          <mat-form-field appearance="outline">
            <mat-label>Performance Start Date</mat-label>
            <input
              matInput
              [matDatepicker]="startPicker"
              formControlName="performanceStartDate"
            />
            <mat-datepicker-toggle
              matIconSuffix
              [for]="startPicker"
            ></mat-datepicker-toggle>
            <mat-datepicker #startPicker></mat-datepicker>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Performance End Date</mat-label>
            <input
              matInput
              [matDatepicker]="endPicker"
              formControlName="performanceEndDate"
            />
            <mat-datepicker-toggle
              matIconSuffix
              [for]="endPicker"
            ></mat-datepicker-toggle>
            <mat-datepicker #endPicker></mat-datepicker>
          </mat-form-field>
        </div>

        @if (isGroupReporting && !isTinRollup) {
          <mat-form-field appearance="outline" class="form-field">
            <mat-label>Providers*</mat-label>
            <mat-select
              formControlName="selectedProviders"
              multiple
              panelClass="searchable-panel"
            >
              <mat-select-trigger>
                @if (allProvidersSelected) {
                  All
                } @else if (
                  tinDetailsForm.get("selectedProviders")?.value?.length
                ) {
                  {{ tinDetailsForm.get("selectedProviders")?.value?.length }}
                  provider(s) selected
                }
              </mat-select-trigger>
              <mat-option disabled>
                <ngx-mat-select-search
                  [formControl]="providerSearchControl"
                  placeholderLabel="Search providers..."
                  noEntriesFoundLabel="No providers found"
                >
                </ngx-mat-select-search>
              </mat-option>

              <mat-option
                (click)="toggleAllProviders()"
                [value]="'all'"
                [class.selected]="allProvidersSelected"
              >
                All
              </mat-option>
              <mat-divider></mat-divider>

              @for (
                provider of filteredProviders$ | async;
                track provider.id;
                let last = $last
              ) {
                <mat-option [value]="provider.id">
                  {{ provider.firstName }} {{ provider.lastName }} - NPI:
                  {{ provider.npi }}
                </mat-option>
                @if (!last) {
                  <mat-divider></mat-divider>
                }
              }
            </mat-select>
            @if (isFieldInvalid("selectedProviders")) {
              <mat-error>Please select at least one provider</mat-error>
            }
          </mat-form-field>
        }

        <div class="document-section">
          <h3 class="document-title">Last Uploaded Document</h3>

          @if (currentPacket()?.tinDetails?.documentName) {
            <div class="uploaded-file">
              <mat-icon class="file-icon">description</mat-icon>
              <span class="file-name">
                {{ currentPacket()?.tinDetails?.documentName }}
              </span>
              <div class="file-actions">
                <button
                  mat-icon-button
                  (click)="viewDocument()"
                  matTooltip="View"
                  class="action-btn read-only-allowed"
                >
                  <mat-icon>visibility</mat-icon>
                </button>
                <button
                  mat-icon-button
                  (click)="downloadDocument()"
                  matTooltip="Download"
                  class="action-btn read-only-allowed"
                >
                  <mat-icon>download</mat-icon>
                </button>
              </div>
            </div>
          } @else {
            <p class="no-document">No document uploaded</p>
          }

          <div class="upload-info">
            <p class="upload-instructions">
              Upload supporting document*: (Acceptable formats include: PDF,
              JPG, PNG, or JPEG. Max File Size: 25MB)
            </p>

            <div class="upload-controls">
              <input
                type="file"
                #fileInput
                (change)="onFileSelected($event)"
                accept=".pdf,.jpg,.jpeg,.png"
                style="display: none"
              />
              <button
                mat-raised-button
                color="primary"
                (click)="fileInput.click()"
                class="upload-button"
              >
                Choose File
              </button>
              <span class="file-status">
                @if (uploadedFileName) {
                  {{ uploadedFileName }}
                } @else {
                  No File Chosen
                }
              </span>
            </div>

            @if (isFieldInvalid("supportingDocument")) {
              <p class="error-text">Please upload a supporting document</p>
            }

            <p class="upload-note">
              Upload a CMS-1500 form for 1 claim for the TIN to which you have
              reassigned billing rights to PECGS for PY 2025.
            </p>
          </div>
        </div>
      </div>
    </form>
  </mat-card-content>
</mat-card>
